(() => {
  "use strict";

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );

  function closeFaqItem(item) {
    const button = item.querySelector("[data-lp-accordion]");
    const answer = item.querySelector(".lp-faq__answer");

    item.classList.remove("is-open");
    button?.setAttribute("aria-expanded", "false");
    if (answer) answer.style.maxHeight = "0px";
  }

  function openFaqItem(item) {
    const button = item.querySelector("[data-lp-accordion]");
    const answer = item.querySelector(".lp-faq__answer");

    item.classList.add("is-open");
    button?.setAttribute("aria-expanded", "true");
    if (answer) {
      const content = answer.querySelector("p");
      const contentHeight = content?.scrollHeight ?? answer.scrollHeight;
      answer.style.maxHeight = `${contentHeight + 100}px`;
    }
  }

  function initLandingFaq(scope = document) {
    scope.querySelectorAll("[data-lp-faq]").forEach((faq) => {
      if (faq.dataset.lpInitialized === "true") return;
      faq.dataset.lpInitialized = "true";

      const items = [...faq.querySelectorAll(".lp-faq__item")];
      items.forEach((item) => {
        const button = item.querySelector("[data-lp-accordion]");
        if (!button) return;

        button.addEventListener("click", () => {
          const wasOpen = item.classList.contains("is-open");
          items.forEach(closeFaqItem);
          if (!wasOpen) openFaqItem(item);
        });
      });
    });
  }

  function initLandingNavigation(scope = document) {
    scope.querySelectorAll("[data-lp-nav]").forEach((nav) => {
      if (nav.dataset.lpInitialized === "true") return;
      nav.dataset.lpInitialized = "true";

      const hero = nav.closest(".lp-hero");
      const toggle = nav.querySelector("[data-lp-menu-toggle]");
      const menu = nav.querySelector("[data-lp-menu]");
      const backdrop = hero?.querySelector("[data-lp-menu-backdrop]");
      const menuLinks = menu ? [...menu.querySelectorAll("a")] : [];
      const navigationLinks = [
        ...nav.querySelectorAll(
          'a[href="#lp-social"], a[href="#lp-about"], a[href="#lp-faq"]',
        ),
      ];
      let scrollSource = null;

      const resolveScrollSource = () => {
        const pageWrapper = document.querySelector(".page-wrapper");
        return window.innerWidth >= 990 && pageWrapper ? pageWrapper : window;
      };

      const setMenuOpen = (open, restoreFocus = false) => {
        if (!toggle || !menu || !backdrop) return;

        nav.classList.toggle("is-menu-open", open);
        menu.classList.toggle("is-open", open);
        toggle.setAttribute("aria-expanded", String(open));
        toggle.setAttribute("aria-label", open ? "close menu" : "menu");
        menu.setAttribute("aria-hidden", String(!open));
        backdrop.hidden = !open;
        document.body.classList.toggle("lp-menu-open", open);

        if (open) menuLinks[0]?.focus({ preventScroll: true });
        if (!open && restoreFocus) toggle.focus({ preventScroll: true });
      };

      const jumpToNavigationTarget = (link) => {
        const hash = link.hash || link.getAttribute("href");
        const target = hash ? document.querySelector(hash) : null;
        if (!target) return;

        const activeScrollSource = scrollSource ?? resolveScrollSource();
        const currentScrollTop =
          activeScrollSource === window
            ? window.scrollY
            : activeScrollSource.scrollTop;
        const sourceTop =
          activeScrollSource === window
            ? 0
            : activeScrollSource.getBoundingClientRect().top;
        const targetScrollTop =
          currentScrollTop +
          target.getBoundingClientRect().top -
          sourceTop -
          60;
        const scrollElement =
          activeScrollSource === window
            ? document.documentElement
            : activeScrollSource;
        const previousScrollBehavior = scrollElement.style?.scrollBehavior;

        if (scrollElement.style) scrollElement.style.scrollBehavior = "auto";
        if (activeScrollSource === window) {
          window.scrollTo({ top: targetScrollTop, behavior: "auto" });
        } else if (activeScrollSource.scrollTo) {
          activeScrollSource.scrollTo({
            top: targetScrollTop,
            behavior: "auto",
          });
        } else {
          activeScrollSource.scrollTop = targetScrollTop;
        }
        if (scrollElement.style) {
          scrollElement.style.scrollBehavior = previousScrollBehavior;
        }
      };

      toggle?.addEventListener("click", () => {
        setMenuOpen(!nav.classList.contains("is-menu-open"), true);
      });
      backdrop?.addEventListener("click", () => setMenuOpen(false, true));
      navigationLinks.forEach((link) => {
        link.addEventListener("click", (event) => {
          event.preventDefault();
          setMenuOpen(false);
          jumpToNavigationTarget(link);
        });
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && nav.classList.contains("is-menu-open")) {
          setMenuOpen(false, true);
        }
      });

      const updateScrollState = () => {
        const scrollTop =
          scrollSource === window
            ? window.scrollY
            : (scrollSource?.scrollTop ?? 0);
        nav.classList.toggle("is-sticky", scrollTop > 60);
      };

      const bindScrollSource = () => {
        const nextScrollSource = resolveScrollSource();
        if (nextScrollSource === scrollSource) return;

        scrollSource?.removeEventListener?.("scroll", updateScrollState);
        scrollSource = nextScrollSource;
        scrollSource.addEventListener("scroll", updateScrollState, {
          passive: true,
        });
      };

      bindScrollSource();
      window.addEventListener("resize", () => {
        if (window.innerWidth >= 768) setMenuOpen(false);
        bindScrollSource();
        updateScrollState();
      });
      updateScrollState();
    });
  }

  /* Each feature panel reveals once as it enters the viewport: the copy and
     panel image rise while the photography fades in (Figma 488:51 annotation). */
  function initLandingReveal(scope = document) {
    scope.querySelectorAll("[data-lp-reveal]").forEach((section) => {
      if (section.dataset.lpRevealInitialized === "true") return;
      section.dataset.lpRevealInitialized = "true";

      if (
        prefersReducedMotion.matches ||
        typeof IntersectionObserver !== "function"
      ) {
        section.classList.add("is-revealed");
        return;
      }

      section.classList.add("is-reveal-ready");

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.15 },
      );

      observer.observe(section);

      section.lpRevealDestroy = () => {
        observer.disconnect();
        section.classList.remove("is-reveal-ready", "is-revealed");
        section.dataset.lpRevealInitialized = "false";
      };
    });
  }

  function initLandingVideo(scope = document) {
    scope.querySelectorAll("[data-lp-video-frame]").forEach((frame) => {
      if (frame.dataset.lpInitialized === "true") return;
      frame.dataset.lpInitialized = "true";

      const trigger = frame.querySelector("[data-lp-video]");
      const playerWrap = frame.querySelector("[data-lp-video-player]");
      const player = playerWrap?.querySelector("video");
      const iframe = playerWrap?.querySelector("iframe");
      if (!trigger || !playerWrap || (!player && !iframe)) return;

      trigger.addEventListener("click", () => {
        if (trigger.disabled) return;
        frame.classList.add("is-playing");
        playerWrap.hidden = false;
        trigger.hidden = true;
        if (iframe?.dataset.src && !iframe.hasAttribute("src")) {
          iframe.src = iframe.dataset.src;
        }
        player?.play().catch(() => {});
      });
    });
  }

  function initLandingPrepCards(scope = document) {
    const prepSections = scope.matches?.("[data-lp-prep-cards]")
      ? [scope]
      : [...scope.querySelectorAll("[data-lp-prep-cards]")];

    prepSections.forEach((prep) => {
      if (prep.dataset.lpPrepInitialized === "true") return;

      const items = [...prep.querySelectorAll("[data-lp-prep-item]")];
      const dots = [...prep.querySelectorAll("[data-lp-prep-dot]")];
      const list = prep.querySelector(".lp-prep-cards__list");
      const mobile = window.matchMedia("(max-width: 767px)");
      if (!items.length || !list) return;

      prep.dataset.lpPrepInitialized = "true";
      let activeIndex = 0;
      let pointerStartX = null;
      let capturedPointerId = null;
      let mobileBound = false;
      let scrollFrame = 0;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const stage = prep.querySelector('.lp-prep-cards__stage');
      const copies = items.map(item => item.querySelector('.lp-prep-cards__copy'));
      const captions = document.createElement('div');
      captions.className = 'lp-prep-cards__captions';
      stage.append(captions);
      const viewport = document.createElement('div');
      viewport.className = 'lp-prep-cards__viewport';
      stage.before(viewport);
      viewport.append(stage);
      let pinGroup = null;
      let pinStrip = null;
      let pinTop = 0;
      const mountPinGroup = () => {
        if (pinGroup) return;
        const wrapper = prep.closest('.shopify-section') || prep;
        const previous = wrapper.previousElementSibling;
        const next = wrapper.nextElementSibling;
        const siblings = [
          previous?.classList.contains('shopify-section') ? previous : null,
          wrapper,
          next?.classList.contains('shopify-section') ? next : null,
        ].filter(Boolean);
        pinGroup = document.createElement('div');
        pinGroup.className = 'lp-prep-pin';
        pinStrip = document.createElement('div');
        pinStrip.className = 'lp-prep-pin__strip';
        siblings[0].before(pinGroup);
        pinGroup.append(pinStrip);
        siblings.forEach(section => pinStrip.append(section));
      };
      const unmountPinGroup = () => {
        if (!pinGroup) return;
        [...pinStrip.children].forEach(section => pinGroup.before(section));
        pinGroup.remove();
        pinGroup = null;
        pinStrip = null;
      };
      const measurePinGroup = (travel) => {
        const wrapper = prep.closest('.shopify-section') || prep;
        const previousHeight = wrapper.getBoundingClientRect().top - pinStrip.getBoundingClientRect().top;
        const centerTop = Math.max(0, (window.innerHeight - viewport.clientHeight) / 2);
        pinTop = centerTop - previousHeight;
        pinGroup.style.setProperty('--lp-prep-pin-top', `${pinTop}px`);
        pinGroup.style.setProperty('--lp-prep-pin-height', `${pinStrip.getBoundingClientRect().height + travel}px`);
      };
      const updateScroll = () => {
        scrollFrame = 0;
        if (!mobile.matches || !pinGroup) return;
        const step = window.innerHeight * 0.85;
        const travel = step * (items.length - 1);
        prep.style.setProperty('--lp-prep-travel', `${travel}px`);
        measurePinGroup(travel);
        const progress = Math.max(0, Math.min(items.length - 1, (pinTop - pinGroup.getBoundingClientRect().top) / step));
        const base = Math.max(0, Math.min(items.length - 2, Math.floor(progress)));
        const fraction = progress - base;
        // Leave a short resting interval at both ends of each cut.
        const t = Math.max(0, Math.min(1, (fraction - 0.15) / 0.7));
        const nextActive = base + (t >= 0.55 ? 1 : 0);
        const changed = nextActive !== activeIndex;
        activeIndex = nextActive;
        render(changed);
        // This pose is controlled directly by scrolling, with no timed animation.
        // Reduced motion disables programmatic smooth scrolling, not card geometry.
        const ease = t * t * (3 - 2 * t);
        items.forEach((item, index) => {
          // Figma 1675:1148 resting poses, compensated for the 50% 125%
          // scroll pivot. Interpolate the back cards into the front pose.
          const poses = [
            { left: 78.18, top: 151.53, rotation: 0 },
            { left: 79.74, top: 153.08, rotation: 3.69 },
            { left: 76.25, top: 155.02, rotation: 8.22 },
          ];
          const depth = Math.max(0, Math.min(2, index - base - ease));
          const near = poses[Math.floor(depth)];
          const far = poses[Math.ceil(depth)];
          const blend = depth % 1;
          const left = near.left + (far.left - near.left) * blend;
          const top = near.top + (far.top - near.top) * blend;
          const rotation = index < base ? -160
            : index === base ? -160 * ease
            : near.rotation + (far.rotation - near.rotation) * blend;
          const fade = Math.max(0, Math.min(1, (t - 0.15) / 0.4));
          const opacity = index < base ? 0
            : index === base ? 1 - fade * fade * (3 - 2 * fade)
            : 1;
          const gone = opacity === 0;
          // Keep the departing card's upper corner below the subtitle as it
          // begins rotating left. Resting poses remain exactly at Figma anchors.
          const departureDrop = index === base ? 32 * Math.sin(-rotation * Math.PI / 180) : 0;
          item.style.setProperty('--lp-prep-top', String((top + departureDrop) * 2));
          item.style.setProperty('--lp-prep-left', String(left * 2));
          item.style.setProperty('--lp-prep-z', String(items.length - index));
          item.style.transform = `rotate(${rotation}deg)`;
          item.style.opacity = String(opacity);
          item.style.visibility = gone ? 'hidden' : 'visible';
        });
      };
      const onScroll = () => {
        if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScroll);
      };

      const positions = [
        { top: 303.06, left: 156.36, rotation: "0deg", z: 3 },
        { top: 306.16, left: 159.48, rotation: "3.69deg", z: 2 },
        { top: 310.04, left: 152.5, rotation: "8.22deg", z: 1 },
      ];

      const clearItemState = (item) => {
        item.classList.remove("is-front");
        delete item.dataset.lpPrepDepth;
        item.style.removeProperty("--lp-prep-depth");
        item.style.removeProperty("--lp-prep-top");
        item.style.removeProperty("--lp-prep-left");
        item.style.removeProperty("--lp-prep-rotation");
        item.style.removeProperty("--lp-prep-z");
        item.style.removeProperty('transform');
        item.style.removeProperty('visibility');
        item.style.removeProperty('opacity');
        item.removeAttribute("aria-hidden");
      };

      const render = (notify = true) => {
        if (!mobile.matches) return;

        prep.classList.add("is-prep-ready");
        items.forEach((item, index) => {
          const depth = (index - activeIndex + items.length) % items.length;
          const position = positions[depth] ?? positions.at(-1);

          item.style.removeProperty('transform');
          item.style.removeProperty('visibility');
          item.style.removeProperty('opacity');
          if (copies[index]) copies[index].hidden = index !== activeIndex;
          item.dataset.lpPrepDepth = String(depth);
          item.style.setProperty("--lp-prep-depth", String(depth));
          item.style.setProperty("--lp-prep-top", String(position.top));
          item.style.setProperty("--lp-prep-left", String(position.left));
          item.style.setProperty("--lp-prep-rotation", position.rotation);
          item.style.setProperty("--lp-prep-z", String(position.z));
          item.classList.toggle("is-front", depth === 0);
          item.setAttribute("aria-hidden", depth === 0 ? "false" : "true");
        });
        dots.forEach((dot, index) => {
          dot.setAttribute("aria-selected", index === activeIndex ? "true" : "false");
        });
        if (notify) document.dispatchEvent(new Event('lp:prep-change'));
      };

      const goTo = (index) => {
        if (!mobile.matches) return;
        const nextIndex = ((Number(index) % items.length) + items.length) % items.length;
        if (!pinGroup) return;
        const top = window.scrollY + pinGroup.getBoundingClientRect().top - pinTop + nextIndex * window.innerHeight * 0.85;
        window.scrollTo({ top, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      };

      const onPointerDown = (event) => {
        pointerStartX = event.clientX;
        capturedPointerId = event.pointerId ?? null;
        if (capturedPointerId !== null && list.setPointerCapture) {
          try {
            list.setPointerCapture(capturedPointerId);
          } catch (_) {}
        }
      };

      const releasePointer = (event) => {
        const pointerId = capturedPointerId ?? event.pointerId ?? null;
        if (pointerId !== null && list.releasePointerCapture) {
          const isHeld = list.hasPointerCapture
            ? list.hasPointerCapture(pointerId)
            : true;
          if (isHeld) {
            try {
              list.releasePointerCapture(pointerId);
            } catch (_) {}
          }
        }
        capturedPointerId = null;
      };

      const onPointerUp = (event) => {
        releasePointer(event);
        if (pointerStartX === null) return;
        const distance = event.clientX - pointerStartX;
        pointerStartX = null;
        if (Math.abs(distance) < 30) return;
        goTo(activeIndex + (distance < 0 ? 1 : -1));
      };

      const onPointerCancel = (event) => {
        releasePointer(event);
        pointerStartX = null;
      };

      const onDragStart = (event) => {
        event.preventDefault();
      };

      const dotHandlers = dots.map((dot) => () => {
        goTo(Number(dot.dataset.lpPrepDot));
      });

      const bindMobile = () => {
        if (mobileBound || !mobile.matches) return;
        mobileBound = true;
        copies.forEach(copy => { if (copy) captions.append(copy); });
        window.addEventListener('scroll', onScroll, { passive: true, capture: true });
        window.addEventListener('resize', onScroll);
        render();
        mountPinGroup();
        updateScroll();
        list.addEventListener("pointerdown", onPointerDown);
        list.addEventListener("pointerup", onPointerUp);
        list.addEventListener("pointercancel", onPointerCancel);
        list.addEventListener("dragstart", onDragStart);
        dots.forEach((dot, index) => {
          dot.addEventListener("click", dotHandlers[index]);
        });
      };

      const unbindMobile = () => {
        if (mobileBound) {
          list.removeEventListener("pointerdown", onPointerDown);
          list.removeEventListener("pointerup", onPointerUp);
          list.removeEventListener("pointercancel", onPointerCancel);
          list.removeEventListener("dragstart", onDragStart);
          dots.forEach((dot, index) => {
            dot.removeEventListener("click", dotHandlers[index]);
          });
        }
        mobileBound = false;
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onScroll);
        cancelAnimationFrame(scrollFrame);
        scrollFrame = 0;
        activeIndex = 0;
        prep.style.removeProperty('--lp-prep-travel');
        unmountPinGroup();
        stage.style.removeProperty('transform');
        if (capturedPointerId !== null) {
          releasePointer({ pointerId: capturedPointerId });
        }
        pointerStartX = null;
        prep.classList.remove("is-prep-ready");
        items.forEach(clearItemState);
        copies.forEach((copy, index) => {
          if (!copy) return;
          copy.hidden = false;
          items[index].querySelector('.lp-prep-cards__card').append(copy);
        });
        dots.forEach((dot, index) => {
          dot.setAttribute("aria-selected", index === 0 ? "true" : "false");
        });
      };

      const onViewportChange = () => {
        if (mobile.matches) bindMobile();
        else unbindMobile();
      };

      prep.lpPrepGoTo = goTo;
      prep.lpPrepDestroy = () => {
        unbindMobile();
        mobile.removeEventListener?.("change", onViewportChange);
        prep.dataset.lpPrepInitialized = "false";
        viewport.before(stage);
        viewport.remove();
        captions.remove();
        delete prep.lpPrepGoTo;
        delete prep.lpPrepDestroy;
      };

      mobile.addEventListener?.("change", onViewportChange);
      onViewportChange();
    });
  }

  /* Subscribe forms: submit the email to Klaviyo and show inline feedback.
     Config comes from Theme settings > Klaviyo subscription (exposed as
     window.MoonphaseKlaviyo in theme.liquid). Primary path is Klaviyo's
     documented client API (POST /client/subscriptions, revision 2024-10-15);
     when the browser blocks or fails that request (e.g. its CORS preflight is
     rejected from non-storefront origins such as a local dev server) we fall
     back to Klaviyo's classic ajax endpoint, which accepts simple
     urlencoded POSTs. The native {% form 'customer' %} POST stays as the
     no-JS fallback. One delegated listener also covers the floating hero form
     after it is portaled to <body>. */
  function lpKlaviyoConfig() {
    const cfg = window.MoonphaseKlaviyo || {};
    return {
      companyId: cfg.companyId || "S7xTWi",
      listId: cfg.listId || "ScxaiB",
      source: cfg.source || "Landing page",
    };
  }

  function lpKlaviyoSubscribe(email) {
    const { companyId, listId, source } = lpKlaviyoConfig();
    const clientApi = fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-10-15",
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: source,
              profile: {
                data: { type: "profile", attributes: { email } },
              },
            },
            relationships: {
              list: { data: { type: "list", id: listId } },
            },
          },
        }),
      },
    ).then((res) => {
      if (!res.ok && res.status !== 202)
        throw new Error(`Klaviyo client API ${res.status}`);
    });
    // Any failure (network/CORS rejection included) retries through the
    // classic endpoint, which needs no custom headers.
    return clientApi.catch(() => {
      return fetch(
        "https://manage.kmail-lists.com/ajax/subscriptions/subscribe",
        {
          method: "POST",
          body: new URLSearchParams({ g: listId, email, $source: source }),
        },
      )
        .then((res) => {
          if (!res.ok) throw new Error(`Klaviyo legacy ${res.status}`);
          return res.json();
        })
        .then((data) => {
          if (!data || data.success !== true)
            throw new Error("Klaviyo rejected the subscription");
        });
    });
  }

  function lpSubscribeMessage(form) {
    let msg = form.querySelector(":scope > .lp-subscribe__msg");
    if (!msg) {
      msg = document.createElement("p");
      msg.className = "lp-subscribe__msg";
      msg.setAttribute("role", "status");
      form.appendChild(msg);
    }
    return msg;
  }

  function initLandingSubscribe() {
    if (document.documentElement.dataset.lpSubscribeInitialized === "true")
      return;
    document.documentElement.dataset.lpSubscribeInitialized = "true";
    document.addEventListener("submit", (event) => {
      const form = event.target;
      if (!form?.classList?.contains("lp-subscribe")) return;
      event.preventDefault();
      if (
        form.dataset.lpSubscribeBusy === "true" ||
        form.classList.contains("is-success")
      ) return;
      const input = form.querySelector('input[type="email"]');
      const button = form.querySelector('button[type="submit"]');
      if (!input || !button) return;
      if (typeof input.reportValidity === "function" && !input.reportValidity())
        return;
      const email = input.value.trim();
      const msg = lpSubscribeMessage(form);
      form.dataset.lpSubscribeBusy = "true";
      form.classList.remove("is-success", "is-error");
      msg.textContent = "";
      button.disabled = true;
      lpKlaviyoSubscribe(email)
        .then(() => {
          if (typeof fbq === "function") {
            fbq("track", "CompleteRegistration");
          }
          form.classList.add("is-success");
          input.value = "";
          input.disabled = true;
          input.hidden = true;
          msg.className = "lp-subscribe__input lp-subscribe__success";
          input.after(msg);
          msg.textContent = "You are subscribed!";
          button.textContent = "✓";
          button.setAttribute("aria-label", "Subscribed");
        })
        .catch(() => {
          form.classList.add("is-error");
          msg.textContent = "Something went wrong. Please try again.";
        })
        .finally(() => {
          delete form.dataset.lpSubscribeBusy;
          button.disabled = form.classList.contains("is-success");
        });
    });
  }

  /* Keeps the authored Hero form in place, then swaps in a separate fixed
     sibling after mobile scrolling begins. The sibling lives outside the
     clipped Hero artwork so later sections cannot hide it. */
  function initLandingFloatingSubscribe(scope = document) {
    const hero = scope.matches?.(".lp-hero")
      ? scope
      : (scope.querySelector?.(".lp-hero") ?? document.querySelector(".lp-hero"));
    const restingForm = hero?.querySelector(".lp-subscribe--hero-resting");
    const floatingForm = scope.matches?.(".lp-subscribe--hero-floating")
      ? scope
      : (scope.querySelector?.(".lp-subscribe--hero-floating") ??
        document.querySelector(".lp-subscribe--hero-floating"));
    const footer = document.querySelector(".lp-footer");
    if (
      !hero ||
      !restingForm ||
      !floatingForm ||
      floatingForm.dataset.lpFloatInitialized === "true"
    ) return;
    // A fixed control must not remain owned by an off-screen Shopify section.
    // Portaling it to body gives it one global containing and stacking context
    // across browser compositor implementations.
    document.body.appendChild(floatingForm);
    floatingForm.dataset.lpFloatInitialized = "true";

    const mobile = window.matchMedia("(max-width: 767px)");
    let footerVisible = false;

    const update = () => {
      const heroRect = hero.getBoundingClientRect();
      const shouldFloat = mobile.matches && heroRect.top < 0 && !footerVisible;
      floatingForm.classList.toggle("is-floating", shouldFloat);
      restingForm.classList.toggle("is-float-replaced", shouldFloat);
    };

    const footerObserver =
      footer && typeof IntersectionObserver === "function"
        ? new IntersectionObserver((entries) => {
            footerVisible = entries.some((entry) => entry.isIntersecting);
            update();
          })
        : null;
    footerObserver?.observe(footer);

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    mobile.addEventListener?.("change", update);
    const destroy = () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      mobile.removeEventListener?.("change", update);
      footerObserver?.disconnect();
      floatingForm.classList.remove("is-floating");
      restingForm.classList.remove("is-float-replaced");
      floatingForm.dataset.lpFloatInitialized = "false";
      floatingForm.remove();
      delete floatingForm.lpFloatDestroy;
      delete restingForm.lpFloatDestroy;
    };
    floatingForm.lpFloatDestroy = destroy;
    restingForm.lpFloatDestroy = destroy;
    update();
  }

  function initLanding(scope = document) {
    initLandingNavigation(scope);
    initLandingReveal(scope);
    initLandingFaq(scope);
    initLandingVideo(scope);
    initLandingPrepCards(scope);
    initLandingSubscribe();
    initLandingFloatingSubscribe(scope);
  }

  window.MoonphaseLanding = {
    init: initLanding,
    initLandingNavigation,
    initLandingReveal,
    initLandingFaq,
    initLandingVideo,
    initLandingPrepCards,
    initLandingSubscribe,
    initLandingFloatingSubscribe,
  };

  initLanding();

  document.addEventListener("shopify:section:load", (event) =>
    initLanding(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    const target = event.target;
    const reveal = target.matches?.("[data-lp-reveal]")
      ? target
      : target.querySelector?.("[data-lp-reveal]");
    const prep = target.matches?.("[data-lp-prep-cards]")
      ? target
      : target.querySelector?.("[data-lp-prep-cards]");
    const floatingController = target.matches?.(".lp-subscribe--hero-resting")
      ? target
      : target.querySelector?.(".lp-subscribe--hero-resting");
    reveal?.lpRevealDestroy?.();
    prep?.lpPrepDestroy?.();
    floatingController?.lpFloatDestroy?.();
  });
  document.addEventListener("shopify:block:select", (event) => {
    const item = event.target.closest?.("[data-lp-prep-item]");
    const prep = item?.closest?.("[data-lp-prep-cards]");
    const index = Number(item?.dataset.lpPrepIndex);
    if (prep && Number.isFinite(index)) prep.lpPrepGoTo?.(index);
  });
})();
